import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import get from 'lodash/get';
import { Controller, FieldValues } from 'react-hook-form';
import { CommonInputProps, KeyByValue } from './InputTypes';
import InputWrapper from './InputWrapper';

type GetOptionIdentifier<TOption extends FieldValues> =
    | ((option: TOption) => string | number)
    | KeyByValue<TOption, number | string>;
type GetOptionLabel<TOption extends FieldValues> =
    | ((option: TOption) => string)
    | KeyByValue<TOption, number | string | null>;

interface Props<TFormData extends FieldValues, TOption extends FieldValues> extends Omit<
    CommonInputProps<TFormData>,
    'validate'
> {
    options: TOption[];
    getOptionLabel: GetOptionLabel<TOption>;
    getOptionIdentifier: GetOptionIdentifier<TOption>;
    size?: 'sm' | 'default';
    /** Returning `false` refuses the pick and leaves the stored value alone. */
    onChange?: (value: TOption | null, previousValue?: TOption | null) => boolean | void;
    /**
     * Identifiers whose row calls `onChange` on every click, including when it is already the
     * selected one — for a row that opens something (a date range dialog) rather than just picking.
     */
    alwaysTriggerOnChangeForValues?: (string | number)[];
    getSelectedValueLabel?: GetOptionLabel<TOption>;
}

const EMPTY_TRIGGER_VALUES: (string | number)[] = [];

const SelectPickerInput = <TFormData extends FieldValues, TOption extends FieldValues>({
    name,
    options,
    label,
    gap,
    required,
    getOptionLabel,
    getOptionIdentifier,
    control,
    wrapperClassName,
    size = 'sm',
    onChange,
    alwaysTriggerOnChangeForValues = EMPTY_TRIGGER_VALUES,
    getSelectedValueLabel,
}: Props<TFormData, TOption>) => {
    const getLabel = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionLabel === 'string') {
            return get(option, getOptionLabel);
        } else {
            return getOptionLabel(option);
        }
    };

    const getSelectedLabel = (option: TOption | null) => {
        if (!option) {
            return null;
        }
        if (getSelectedValueLabel) {
            if (typeof getSelectedValueLabel === 'string') {
                return get(option, getSelectedValueLabel);
            } else {
                return getSelectedValueLabel(option);
            }
        }
        return getLabel(option);
    };

    const getValue = (option: TOption | null) => {
        if (!option) {
            return null;
        }

        if (typeof getOptionIdentifier === 'string') {
            return get(option, getOptionIdentifier);
        } else {
            return getOptionIdentifier(option);
        }
    };

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange: innerOnChange, value }, fieldState: { error } }) => {
                // The field stores `{value, label}`, not the option object the list was built from.
                const toStoredValue = (option: TOption) =>
                    ({ value: getValue(option), label: getLabel(option) }) as unknown as TOption;

                const pick = (option: TOption) => {
                    const newValue = toStoredValue(option);
                    if (onChange) {
                        if (onChange(newValue, value) === false) {
                            return;
                        }
                    }
                    innerOnChange(newValue);
                };

                return (
                    <InputWrapper
                        name={name}
                        hasErrors={!!error}
                        required={required}
                        noFullWidthInputWrapper
                        className={wrapperClassName}
                        label={label}
                        gap={gap}
                    >
                        <Select<string | number>
                            value={getValue(value) ?? null}
                            onValueChange={(next) => {
                                // An `alwaysTriggerOnChangeForValues` row reports every click from
                                // its own onClick. Base UI skips this callback when such a row moves
                                // the value, but still fires it when the row re-picks the value that
                                // is already stored — so handling it here too would run the
                                // consumer's handler twice on exactly that click.
                                if (next === null || alwaysTriggerOnChangeForValues.includes(next)) {
                                    return;
                                }
                                const option = options.find((candidate) => getValue(candidate) === next);
                                if (option) {
                                    pick(option);
                                }
                            }}
                        >
                            <SelectTrigger id={name} size={size} className="font-semibold" aria-invalid={!!error}>
                                <SelectValue>{() => getSelectedLabel(value)}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {options.map((option) => {
                                    const optionValue = getValue(option);
                                    // An empty string clears a Base UI Select, so it stays excluded —
                                    // but a numeric 0 is a real identifier.
                                    if (optionValue == null || optionValue === '') {
                                        return null;
                                    }

                                    return (
                                        <SelectItem
                                            key={optionValue}
                                            value={optionValue}
                                            title={String(getLabel(option) ?? '') || undefined}
                                            onClick={
                                                alwaysTriggerOnChangeForValues.includes(optionValue)
                                                    ? () => pick(option)
                                                    : undefined
                                            }
                                        >
                                            {getLabel(option)}
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </InputWrapper>
                );
            }}
        />
    );
};

export default SelectPickerInput;
